import React, {useState, useEffect, useMemo} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {refreshUsersData, getSettingsData} from '../Settings/settingsSlice';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  Switch,
  Alert,
  AlertTitle,
  Collapse,
  Snackbar,
  CircularProgress,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {MuiChipsInput} from 'mui-chips-input';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {fetchPostAuthSafe, emailRegex, sortVehicleNamesHelper, getUserDisplayName} from '../../app/utils';
import {machineTypeMapping} from '../../app/utils';
import {IntelliSearchSelect} from '../../components/IntelliSearchSelect';
import {GeneralSearchSelect, InlineTooltip} from '../../components/GeneralComponents';
import {AdapterLuxon} from '@mui/x-date-pickers/AdapterLuxon';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {TimePicker} from '@mui/x-date-pickers/TimePicker';
import {DateTime} from 'luxon';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Build a cron schedule string matching the existing changeCronSchedule() logic in ReportEditModal.
 * @param {string} range - daily | weekly | bi-weekly | monthly | quarterly
 * @param {number} minute - customer minuteOffSet
 * @param {number} hour - (customerSettings.general.hourOffSet + 1) % 24
 * @param {number} dayOfWeek - 0-6 (Sun-Sat), used for weekly/bi-weekly
 * @return {string} 5-field cron string
 */
function buildCronSchedule(range, minute, hour, dayOfWeek) {
  switch (range) {
    case 'weekly':
    case 'bi-weekly':
      return `${minute} ${hour} * * ${dayOfWeek}`;
    case 'monthly':
      return `${minute} ${hour} 1 * *`;
    case 'quarterly':
      return `${minute} ${hour} 1 1,4,7,10 *`;
    case 'daily':
    default:
      return `${minute} ${hour} * * *`;
  }
}

/**
 * Coerce a chat message timestamp (ISO string from a live stream, or unix-ms number
 * from reloaded history) into a unix-ms number, per the report config schema.
 * @param {string|number|undefined} ts
 * @return {number} unix ms
 */
function normalizeTimestampMs(ts) {
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'string') {
    const parsed = new Date(ts).getTime();
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Date.now();
}

/**
 * Mini modal for saving a chat analysis as a recurring report config.
 * Technical data (SQL, HTML, text) is passed from the message object and included
 * in the payload transparently — the user only sees name, frequency, and delivery settings.
 * @return {JSX.Element} The modal component
 */
export default function SaveAsReportModal({open, onClose, threadId, msg}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userSettings = useSelector((state) => {
    return state.app.userSettings;
  });
  const customerSettings = useSelector((state) => {
    return state.app.customerSettings;
  });
  const databaseUsers = useSelector((state) => {
    return state.settings.databaseUsers;
  });
  const vehicles = useSelector((state) => {
    return state.settings.vehiclesUnfiltered;
  });
  const zonesDicts = useSelector((state) => {
    return state.settings.zonesDicts;
  });

  // ── Access control ───────────────────────────────────────────────────────
  // ic_admin or users with no personal filters have full access to select any user.
  // Users with limited permissions can only assign to themselves.
  const hasPersonalFilters =
    (userSettings.general.includedVehicles || []).length > 0 ||
    (userSettings.general.includedBlocks || []).length > 0 ||
    (userSettings.general.includedFields || []).length > 0 ||
    (userSettings.general.includedRegions || []).length > 0;
  const isAdmin = userSettings.role === 'ic_admin';
  const hasFullAccess = isAdmin || !hasPersonalFilters;

  // ── Load users and settings data into Redux if not already present ──────────
  useEffect(() => {
    if (open) {
      if (!databaseUsers || databaseUsers.length === 0) {
        dispatch(refreshUsersData());
      }
      if (!vehicles || vehicles.length === 0) {
        dispatch(getSettingsData());
      }
    }
  }, [open, databaseUsers, vehicles, dispatch]);

  // ── Fetch existing aiAnalysis configs when modal opens ────────────────────
  const [existingAiConfigs, setExistingAiConfigs] = useState([]);

  function normalizeReportConfig(config) {
    return config?.data || config;
  }

  useEffect(() => {
    if (open) {
      fetchPostAuthSafe(
        '/settings/getReportConfigs',
        {method: 'GET', headers: {'Content-Type': 'application/json'}},
        userSettings.username,
        userSettings.databaseName
      )
        .then((res) => {
          return res.json();
        })
        .then((configsDict) => {
          if (configsDict && typeof configsDict === 'object') {
            const aiConfigs = Object.values(configsDict)
              .map(normalizeReportConfig)
              .filter((c) => {
                return c.reportType === 'aiAnalysis' && !c.disabled;
              });
            setExistingAiConfigs(aiConfigs);
          }
        })
        .catch((err) => {
          console.error('[SaveAsReport] Failed to load report configs:', err);
        });
    }
  }, [open, userSettings.username, userSettings.databaseName]);

  // ── Form state ──────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [range, setRange] = useState('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(1); // Monday
  const [deliveryHour, setDeliveryHour] = useState(null); // null = use customer default
  const [deliveryMinute, setDeliveryMinute] = useState(null);
  const [useDeliveryUserList, setUseDeliveryUserList] = useState(true);
  const [toUsers, setToUsers] = useState([]);
  const [toEmails, setToEmails] = useState([]);
  const [useUserFilters, setUseUserFilters] = useState(true);
  const [includedVehicles, setIncludedVehicles] = useState([]);
  const [includedBlocks, setIncludedBlocks] = useState([]);
  const [includedFields, setIncludedFields] = useState([]);
  const [includedRegions, setIncludedRegions] = useState([]);
  const [timeRangeMode, setTimeRangeMode] = useState('fixed');

  const [overwriteConfigId, setOverwriteConfigId] = useState(null);

  const [submitStatus, setSubmitStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [warningVisible, setWarningVisible] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setName('');
      setRange('weekly');
      setDayOfWeek(1);
      setDeliveryHour(null);
      setDeliveryMinute(null);
      setUseDeliveryUserList(true);
      // Limited users: prepopulate with themselves and lock delivery to user-based
      if (hasFullAccess) {
        setToUsers([]);
      } else {
        setToUsers([userSettings.uid]);
      }
      setToEmails([]);
      setUseUserFilters(true);
      setIncludedVehicles([]);
      setIncludedBlocks([]);
      setIncludedFields([]);
      setIncludedRegions([]);
      setOverwriteConfigId(null);
      setSubmitStatus('idle');
      setErrorMsg('');
      setWarningVisible(false);
    }
  }, [open, hasFullAccess, userSettings.uid]);

  // ── Detect name collision with existing aiAnalysis configs ──────────────

  // Show warning when useUserFilters is toggled off
  useEffect(() => {
    setWarningVisible(!useUserFilters);
  }, [useUserFilters]);

  // Sync useUserFilters with useDeliveryUserList (same as ReportEditModal)
  useEffect(() => {
    setUseUserFilters(useDeliveryUserList);
  }, [useDeliveryUserList]);

  // ── Derived data ────────────────────────────────────────────────────────
  const vehicleOptions = useMemo(() => {
    if (!vehicles) return [];
    const opts = [];
    for (let i = 0; i < vehicles.length; i++) {
      if (vehicles[i].icActive) {
        opts.push({
          value: vehicles[i].serialNumber.toLowerCase(),
          label: vehicles[i].name || vehicles[i].serialNumber,
          group: machineTypeMapping[vehicles[i].machineType],
        });
      }
    }
    opts.sort((a, b) => {
      return sortVehicleNamesHelper(a.label, b.label);
    });
    return opts;
  }, [vehicles]);

  const hasRecipients = useDeliveryUserList ? toUsers.length > 0 : toEmails.length > 0;
  const canSubmit = name.trim().length > 0 && hasRecipients && submitStatus !== 'submitting';

  const defaultMinute = customerSettings?.general?.minuteOffSet || 0;
  const defaultHour = ((customerSettings?.general?.hourOffSet || 0) + 1) % 24;
  const effectiveHour = deliveryHour ?? defaultHour;
  const effectiveMinute = deliveryMinute ?? defaultMinute;

  // ── Submit ──────────────────────────────────────────────────────────────
  async function handleSubmit() {
    setSubmitStatus('submitting');
    setErrorMsg('');

    const schedule = buildCronSchedule(range, effectiveMinute, effectiveHour, dayOfWeek);

    // If overwriting an existing config (explicitly selected from dropdown), include its configId
    const effectiveOverwriteId = overwriteConfigId;

    const payload = {
      ...(effectiveOverwriteId ? {configId: effectiveOverwriteId} : {}),
      name: name.trim(),
      reportType: 'aiAnalysis',
      configType: 'user',
      disabled: false,
      schedule,
      range,
      biWeeklyAnchorDate: '',
      useDeliveryUserList,
      dataRangeStartTimeHour: 0,
      dataRangeStartTimeMin: 0,
      dataRangeEndTimeHour: 0,
      dataRangeEndTimeMin: 0,
      useEndTime: false,
      delivery: {
        method: 'email',
        toUsers: useDeliveryUserList ? toUsers : [],
        toEmails: !useDeliveryUserList ? toEmails : [],
        bccEmails: [],
      },
      aiAnalysis: {
        dataQuery: msg.dataQuery,
        threadId,
        turnId: msg.turnId,
        timestamp: normalizeTimestampMs(msg.timestamp ?? msg.createdAt),
        htmlResponse: msg.html || null,
        textResponse: msg.content || null,
        timeRangeMode,
      },
      filters: {
        useUserFilters,
        includedVehicles,
        includedBlocks,
        includedFields,
        includedRegions,
      },
      settings: {},
    };

    try {
      const options = {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        redirect: 'follow',
        body: JSON.stringify(payload),
      };
      const response = await fetchPostAuthSafe(
        '/settings/createUpdateReportConfigs',
        options,
        userSettings.username,
        userSettings.databaseName
      );
      const result = await response.json();

      if (result.errorMsg) {
        navigate('/error', {state: {errorMsg: result.errorMsg}});
        return;
      }
      if (result.status === 401) {
        navigate('/error', {
          state: {errorMsg: 'Unauthorized Access or Action Detected, Please try again'},
        });
        return;
      }
      if (result.success) {
        if (result.response && typeof result.response === 'object') {
          const aiConfigs = Object.values(result.response)
            .map(normalizeReportConfig)
            .filter((c) => {
              return c.reportType === 'aiAnalysis' && !c.disabled;
            });
          setExistingAiConfigs(aiConfigs);
        }
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setSubmitStatus('error');
        setErrorMsg(result.error || 'Failed to save report configuration.');
      }
    } catch (err) {
      setSubmitStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          Save as Insights Report
          <IconButton size='small' onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 2}}>
          {/* ── Overwrite existing report (optional) ── */}
          {existingAiConfigs.length > 0 && (
            <Box>
              <label className='mb-0'>
                <strong>Overwrite Existing Report</strong>
                <InlineTooltip
                  title='Select an existing saved analysis to overwrite it with this response.'
                  icon={<FontAwesomeIcon icon='fas fa-info-circle' size='1x' style={{color: '#858796'}} />}
                />
              </label>
              <Select
                value={overwriteConfigId || ''}
                onChange={(e) => {
                  const selectedId = e.target.value || null;
                  setOverwriteConfigId(selectedId);
                  if (selectedId) {
                    const existing = existingAiConfigs.find((c) => {
                      return c.configId === selectedId;
                    });
                    if (existing) {
                      setName(existing.name || '');
                      setRange(existing.range || 'weekly');
                      // Extract day of week from schedule cron
                      const cronParts = (existing.schedule || '').split(' ');
                      const dow = parseInt(cronParts[4], 10);
                      setDayOfWeek(!isNaN(dow) && dow >= 0 && dow <= 6 ? dow : 1);
                      const cronHr = parseInt(cronParts[1], 10);
                      const cronMn = parseInt(cronParts[0], 10);
                      if (!isNaN(cronHr)) setDeliveryHour(cronHr);
                      if (!isNaN(cronMn)) setDeliveryMinute(cronMn);
                      setUseDeliveryUserList(existing.useDeliveryUserList !== false);
                      setToUsers(existing.delivery?.toUsers || []);
                      setToEmails(existing.delivery?.toEmails || []);
                      setUseUserFilters(existing.filters?.useUserFilters !== false);
                      setIncludedVehicles(existing.filters?.includedVehicles || []);
                      setIncludedBlocks(existing.filters?.includedBlocks || []);
                      setIncludedFields(existing.filters?.includedFields || []);
                      setIncludedRegions(existing.filters?.includedRegions || []);
                      setTimeRangeMode(existing.aiAnalysis?.timeRangeMode || 'fixed');
                    }
                  }
                }}
                size='small'
                fullWidth
                displayEmpty
              >
                <MenuItem value=''>
                  <em>Create new report</em>
                </MenuItem>
                {existingAiConfigs.map((c) => {
                  return (
                    <MenuItem key={c.configId} value={c.configId}>
                      {c.name} ({c.range})
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
          )}

          {/* ── Report Name ── */}
          <TextField
            label='Report Name'
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            size='small'
            fullWidth
            required
            autoFocus
          />

          {/* ── Frequency ── */}
          <Box sx={{display: 'flex', gap: 2}}>
            <Box sx={{flex: 1}}>
              <label className='mb-0'>
                <strong>Frequency</strong>
              </label>
              <Select
                value={range}
                onChange={(e) => {
                  setRange(e.target.value);
                }}
                size='small'
                fullWidth
              >
                <MenuItem value='daily'>Daily</MenuItem>
                <MenuItem value='weekly'>Weekly</MenuItem>
                <MenuItem value='bi-weekly'>Bi-Weekly</MenuItem>
                <MenuItem value='monthly'>Monthly</MenuItem>
                <MenuItem value='quarterly'>Quarterly</MenuItem>
              </Select>
            </Box>
            {(range === 'weekly' || range === 'bi-weekly') && (
              <Box sx={{flex: 1}}>
                <label className='mb-0'>
                  <strong>Day of Week</strong>
                </label>
                <Select
                  value={dayOfWeek}
                  onChange={(e) => {
                    setDayOfWeek(e.target.value);
                  }}
                  size='small'
                  fullWidth
                >
                  {DAYS_OF_WEEK.map((day, idx) => {
                    return (
                      <MenuItem key={idx} value={idx}>
                        {day}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>
            )}
            <Box sx={{flex: 1}}>
              <label className='mb-0'>
                <strong>Delivery Time</strong>
                <InlineTooltip
                  title='The time of day the report will be generated and emailed.'
                  icon={<FontAwesomeIcon icon='fas fa-info-circle' size={'1x'} style={{color: '#858796'}} />}
                />
              </label>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <TimePicker
                  minutesStep={10}
                  size='small'
                  slotProps={{textField: {size: 'small', fullWidth: true}}}
                  value={DateTime.now().set({hour: effectiveHour, minute: effectiveMinute})}
                  onChange={(e) => {
                    if (!e || !e.isValid) return;
                    if (!Number.isFinite(e.hour) || !Number.isFinite(e.minute)) return;
                    setDeliveryHour(e.hour);
                    setDeliveryMinute(Math.floor(e.minute / 10) * 10);
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          {/* ── Time Range Mode ── */}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <label className='mb-0'>
              <strong style={{opacity: timeRangeMode === 'fixed' ? 1 : 0.5}}>Fixed Period</strong>
              <InlineTooltip
                title='Each run covers only the report frequency period. E.g., a daily report covers just the previous day.'
                icon={<FontAwesomeIcon icon='fas fa-info-circle' size={'1x'} style={{color: '#858796'}} />}
              />
            </label>
            <Switch
              checked={timeRangeMode === 'trailing'}
              onChange={(e) => {
                setTimeRangeMode(e.target.checked ? 'trailing' : 'fixed');
              }}
              size='small'
            />
            <label className='mb-0'>
              <strong style={{opacity: timeRangeMode === 'trailing' ? 1 : 0.5}}>Trailing Period</strong>
              <InlineTooltip
                title='Each run covers the same time span as the original analysis, ending today. E.g., a 90-day analysis on a daily schedule shows the last 90 days each run.'
                icon={<FontAwesomeIcon icon='fas fa-info-circle' size={'1x'} style={{color: '#858796'}} />}
              />
            </label>
          </Box>

          <Divider />

          {/* ── Deliver Based on User toggle (full access only) ── */}
          {hasFullAccess && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <label className='mb-0'>
                <strong>Deliver Based on User</strong>
                <InlineTooltip
                  title='Deliver Report based on User Account instead of Email.'
                  icon={<FontAwesomeIcon icon='fas fa-info-circle' size='1x' style={{color: '#858796'}} />}
                />
              </label>
              <Switch
                checked={useDeliveryUserList}
                onChange={(e) => {
                  setUseDeliveryUserList(e.target.checked);
                }}
              />
            </Box>
          )}

          {/* ── Recipients ── */}
          {useDeliveryUserList ? (
            <Box>
              <label className='mb-0'>
                <strong>Username (Email)</strong>
                <InlineTooltip
                  title='The Users selected will receive this Report.'
                  icon={<FontAwesomeIcon icon='fas fa-info-circle' size='1x' style={{color: '#858796'}} />}
                />
              </label>
              {hasFullAccess ? (
                <GeneralSearchSelect
                  multiple={true}
                  name='delivery.toUsers'
                  value={toUsers}
                  options={(databaseUsers || [])
                    .filter((user) => {
                      return !user.archived || toUsers.includes(user.uid);
                    })
                    .map((user) => {
                      return {value: user.uid, label: `${getUserDisplayName(user, user.uid)} (${user.email})`};
                    })}
                  handleChange={(e) => {
                    setToUsers(e.target.value);
                  }}
                />
              ) : (
                <TextField size='small' fullWidth disabled value={`${userSettings.username} (${userSettings.email})`} />
              )}
            </Box>
          ) : (
            <Box>
              <label className='mb-0'>
                <strong>Email</strong>
                <InlineTooltip
                  title='Emails Entered will receive this Report.'
                  icon={<FontAwesomeIcon icon='fas fa-info-circle' size='1x' style={{color: '#858796'}} />}
                />
              </label>
              <MuiChipsInput
                size='small'
                fullWidth
                hideClearAll
                disableDeleteOnBackspace
                inputProps={{enterKeyHint: 'go'}}
                value={toEmails}
                onChange={(value) => {
                  setToEmails([...value]);
                }}
                validate={(newValue) => {
                  return {
                    isError: !emailRegex.test(newValue),
                    textError: 'Please enter an email address',
                  };
                }}
              />
            </Box>
          )}

          <Divider />

          {/* ── Filters section (only when delivering by user + full access) ── */}
          {useDeliveryUserList && hasFullAccess && (
            <>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <label className='mb-0'>
                  <strong>Apply User Filters</strong>
                  <InlineTooltip
                    title={
                      'When on, each user receives data filtered by their personal settings. ' +
                      'When off, report configuration filters are used instead.'
                    }
                    icon={<FontAwesomeIcon icon='fas fa-info-circle' size='1x' style={{color: '#858796'}} />}
                  />
                </label>
                <Switch
                  checked={useUserFilters}
                  onChange={(e) => {
                    setUseUserFilters(e.target.checked);
                  }}
                />
              </Box>

              {!useUserFilters && (
                <>
                  {warningVisible && (
                    <Alert
                      variant='outlined'
                      severity='warning'
                      sx={{mb: 1}}
                      action={
                        <IconButton
                          size='small'
                          onClick={() => {
                            setWarningVisible(false);
                          }}
                        >
                          <CloseIcon fontSize='inherit' />
                        </IconButton>
                      }
                    >
                      <AlertTitle>Warning</AlertTitle>
                      User settings and filters will be overridden by Report Configuration settings and filters.
                    </Alert>
                  )}

                  <Box>
                    <label className='mb-0'>
                      <strong>Included Vehicles</strong>
                      <InlineTooltip
                        title='The Vehicles to be included in this report. Leave blank to include all.'
                        icon={<FontAwesomeIcon icon='fas fa-info-circle' size='1x' style={{color: '#858796'}} />}
                      />
                    </label>
                    <IntelliSearchSelect
                      multiple={true}
                      name='filters.includedVehicles'
                      value={includedVehicles}
                      options={vehicleOptions}
                      onChange={(e) => {
                        setIncludedVehicles(e.target.value);
                      }}
                      allNoneEnabled
                      returnFullEvent
                      optionDisplayLimit={1}
                      search={true}
                    />
                  </Box>

                  <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                    <Box sx={{flex: 1, minWidth: 200}}>
                      <label className='mb-0'>
                        <strong>Included Blocks</strong>
                        <InlineTooltip
                          title='The Blocks to be included. Leave blank to include all.'
                          icon={<FontAwesomeIcon icon='fas fa-info-circle' size='1x' style={{color: '#858796'}} />}
                        />
                      </label>
                      <IntelliSearchSelect
                        multiple={true}
                        name='filters.includedBlocks'
                        value={includedBlocks}
                        options={
                          zonesDicts
                            ? Object.values(zonesDicts.blocks).map((zone) => {
                                return {value: zone.id, label: zone.name};
                              })
                            : []
                        }
                        onChange={(e) => {
                          setIncludedBlocks(e.target.value);
                        }}
                        allNoneEnabled
                        returnFullEvent
                        optionDisplayLimit={1}
                        search={true}
                      />
                    </Box>
                    <Box sx={{flex: 1, minWidth: 200}}>
                      <label className='mb-0'>
                        <strong>Included Fields</strong>
                        <InlineTooltip
                          title='The Fields to be included. Leave blank to include all.'
                          icon={<FontAwesomeIcon icon='fas fa-info-circle' size='1x' style={{color: '#858796'}} />}
                        />
                      </label>
                      <IntelliSearchSelect
                        multiple={true}
                        name='filters.includedFields'
                        value={includedFields}
                        options={
                          zonesDicts
                            ? Object.values(zonesDicts.fields).map((zone) => {
                                return {value: zone.id, label: zone.name};
                              })
                            : []
                        }
                        onChange={(e) => {
                          setIncludedFields(e.target.value);
                        }}
                        allNoneEnabled
                        returnFullEvent
                        optionDisplayLimit={1}
                        search={true}
                      />
                    </Box>
                    <Box sx={{flex: 1, minWidth: 200}}>
                      <label className='mb-0'>
                        <strong>Included Regions</strong>
                        <InlineTooltip
                          title='The Regions to be included. Leave blank to include all.'
                          icon={<FontAwesomeIcon icon='fas fa-info-circle' size='1x' style={{color: '#858796'}} />}
                        />
                      </label>
                      <IntelliSearchSelect
                        multiple={true}
                        name='filters.includedRegions'
                        value={includedRegions}
                        options={
                          zonesDicts
                            ? Object.values(zonesDicts.regions).map((zone) => {
                                return {value: zone.id, label: zone.name};
                              })
                            : []
                        }
                        onChange={(e) => {
                          setIncludedRegions(e.target.value);
                        }}
                        allNoneEnabled
                        returnFullEvent
                        optionDisplayLimit={1}
                        search={true}
                      />
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}

          {submitStatus === 'error' && (
            <Alert severity='error' sx={{mt: 1}}>
              {errorMsg}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{px: 3, py: 2}}>
          <Button onClick={onClose} variant='outlined'>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant='contained' disabled={!canSubmit}>
            {submitStatus === 'submitting' ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={submitStatus === 'success'}
        autoHideDuration={3000}
        onClose={() => {
          setSubmitStatus('idle');
        }}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert severity='success' variant='filled'>
          Insights report saved successfully.
        </Alert>
      </Snackbar>
    </>
  );
}
